import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, MessageCircle, Mail, Phone, MapPin, ArrowRight, Clock, Shield, Award } from 'lucide-react';

export const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-slate-900 to-slate-950 text-slate-300 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="/logo-pcs.png" 
                alt="PCS Dutos e Exaustores" 
                className="h-20 md:h-24 lg:h-28 w-auto"
              />
            </div>
            <p className="text-slate-400 mb-6 leading-relaxed">
              Há mais de 15 anos oferecendo qualidade e durabilidade em dutos e exaustores industriais para todo o Brasil.
            </p>
            <div className="flex space-x-3">
              {[
                { icon: Facebook, href: 'https://www.facebook.com/share/1Cox2so67x/?mibextid=wwXIfr' },
                { icon: Instagram, href: 'https://www.instagram.com/pcsdutoseexaustores?igsh=MTgzNzdiMHpyN3BmZg%3D%3D&utm_source=qr' },
                { icon: MessageCircle, href: 'https://wa.me/5521971956796' },
              ].map((social, i) => (
                <a 
                  key={i}
                  href={social.href} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-gradient-to-br hover:from-blue-600 hover:to-cyan-600 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-blue-500/25"
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white text-lg font-bold mb-6 flex items-center">
              <span className="w-8 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 mr-3"></span>
              Links Rápidos
            </h3>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Início' },
                { to: '/shop', label: 'Loja' },
                { to: '/#about', label: 'Sobre Nós' },
                { to: '/#contact', label: 'Contato' },
              ].map((link, i) => (
                <li key={i}>
                  <Link 
                    to={link.to} 
                    className="group flex items-center text-slate-400 hover:text-white transition-colors"
                  >
                    <ArrowRight className="h-4 w-4 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="text-white text-lg font-bold mb-6 flex items-center">
              <span className="w-8 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 mr-3"></span>
              Atendimento
            </h3>
            <ul className="space-y-3">
              {[
                { to: '/devolucao', label: 'Política de Devolução' },
                { to: '/termos', label: 'Termos de Uso' },
                { to: '/privacidade', label: 'Privacidade' },
                { to: '/faq', label: 'FAQ' },
              ].map((link, i) => (
                <li key={i}>
                  <Link 
                    to={link.to} 
                    className="group flex items-center text-slate-400 hover:text-white transition-colors"
                  >
                    <ArrowRight className="h-4 w-4 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white text-lg font-bold mb-6 flex items-center">
              <span className="w-8 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 mr-3"></span>
              Contato
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 group">
                <div className="w-10 h-10 bg-slate-800 group-hover:bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                  <MapPin className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <span className="text-white font-medium block">Endereço</span>
                  <span className="text-slate-400 text-sm">Estrada da Palhada 3392 - Nova Iguaçu - RJ</span>
                </div>
              </li>
              <li className="flex items-start space-x-3 group">
                <div className="w-10 h-10 bg-slate-800 group-hover:bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                  <Phone className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <span className="text-white font-medium block">Telefone</span>
                  <span className="text-slate-400 text-sm">(21) 97195-6796</span>
                </div>
              </li>
              <li className="flex items-start space-x-3 group">
                <div className="w-10 h-10 bg-slate-800 group-hover:bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                  <Mail className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <span className="text-white font-medium block">Email</span>
                  <span className="text-slate-400 text-sm">pcsdutoseexaustores@gmail.com</span>
                </div>
              </li>
              <li className="flex items-start space-x-3 group">
                <div className="w-10 h-10 bg-slate-800 group-hover:bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                  <Clock className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <span className="text-white font-medium block">Horário</span>
                  <span className="text-slate-400 text-sm">08:00 às 17:00</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              &copy; {new Date().getFullYear()} Pedro Gomes. Todos os direitos reservados.
            </p>
            <div className="flex items-center space-x-6 text-sm text-slate-500">
              <Link to="/termos" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacidade" className="hover:text-white transition-colors">Privacidade</Link>
              <Link to="/" className="hover:text-white transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};